import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress, Chip,
  List, ListItem, ListItemText, Divider, Grid, Collapse, ToggleButton, ToggleButtonGroup,
  Autocomplete, FormControl, InputLabel, Select, MenuItem, Accordion,
  AccordionSummary, AccordionDetails, Checkbox, FormControlLabel, Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import { searchParties } from '../services/api';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';

export default function PartiesSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchMode, setSearchMode] = useState(searchParams.get('mode') || 'simple');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  // Advanced form state
  const [form, setForm] = useState({
    keywords: '',
    name: '',
    abbreviation: '',
    active: 'all'
  });

  // Available options for autocomplete
  const [availableOptions, setAvailableOptions] = useState({
    names: [],
    abbreviations: []
  });

  // Facets state.
  // `facets` is now driven by the backend response (corpus-wide counts that
  // ignore the currently-applied active/chamber filters, per CLAUDE.md's
  // facet-counts standard). `selectedFacets` holds the user's current
  // selections.
  //
  // Default selection: `active=['Active']` on first mount so the page lands
  // showing only currently-active parties (the primary discriminator). User
  // can uncheck to see all.
  const [facets, setFacets] = useState({ active: [], chambers: [], firstLetter: {} });
  const [selectedFacets, setSelectedFacets] = useState({ active: ['Active'] });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });

  // Compute first-letter facet client-side from hits. Active + Chamber facets
  // come from the backend and ignore filters by design (always show the full
  // corpus counts).
  const computeFirstLetter = (hits) => {
    const firstLetter = {};
    hits.forEach(hit => {
      if (hit.Name) {
        const letter = hit.Name.charAt(0).toUpperCase();
        if (/[A-Z]/.test(letter)) {
          firstLetter[letter] = (firstLetter[letter] || 0) + 1;
        } else {
          firstLetter['#'] = (firstLetter['#'] || 0) + 1;
        }
      }
    });
    return firstLetter;
  };

  // Filter the (already-backend-active/chamber-filtered) hits by the
  // client-side First-Letter facet only.
  const filterResults = (hits) => {
    if (!selectedFacets.firstLetter || selectedFacets.firstLetter.length === 0) {
      return hits;
    }
    return hits.filter(hit => {
      if (!hit.Name) return false;
      const letter = hit.Name.charAt(0).toUpperCase();
      const letterKey = /[A-Z]/.test(letter) ? letter : '#';
      return selectedFacets.firstLetter.includes(letterKey);
    });
  };

  const handleFacetClick = (facetType, value) => {
    setSelectedFacets(prev => {
      const current = prev[facetType] || [];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [facetType]: updated };
    });
    setPage(1);
  };

  const clearAllFilters = () => {
    setSelectedFacets({});
    setPage(1);
  };

  // Fetch autocomplete options on mount. Fetch is unfiltered (no active/
  // chamber params) so the dropdowns cover the whole party corpus.
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const data = await searchParties('*', 1, 100);
        if (data.hits) {
          const names = [...new Set(data.hits.map(h => h.Name).filter(Boolean))].sort();
          const abbreviations = [...new Set(data.hits.map(h => h.Abbrev).filter(Boolean))].sort();
          setAvailableOptions({ names, abbreviations });
        }
      } catch (error) {
        console.error('Failed to fetch options:', error);
      }
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery !== '*') {
      handleSearch(urlQuery);
    } else {
      handleSearch('*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch from backend whenever the active or chamber selection changes
  // (these filters are server-side). First-Letter is client-only and just
  // re-renders. Skip the first render because the bootstrap effect above
  // already kicks off the initial fetch.
  const isFirstFilterRender = React.useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    (selectedFacets.active || []).join(','),
    (selectedFacets.chamber || []).join(',')
  ]);

  const handleChange = (field) => (event) => {
    setForm({ ...form, [field]: event.target.value });
  };

  const handleAutocompleteChange = (field) => (event, newValue) => {
    setForm({ ...form, [field]: newValue || '' });
  };

  const buildAdvancedQuery = () => {
    const parts = [];

    if (form.keywords) parts.push(`(${form.keywords})`);
    if (form.name) parts.push(`Name:"${form.name}"`);
    if (form.abbreviation) parts.push(`Abbrev:"${form.abbreviation}"`);
    if (form.active !== 'all') {
      parts.push(`Active:${form.active}`);
    }

    return parts.length > 0 ? parts.join(' AND ') : '*';
  };

  const handleSearch = async (searchQuery = null) => {
    let q;
    if (searchMode === 'simple') {
      q = searchQuery !== null ? searchQuery : (query || '*');
    } else {
      q = buildAdvancedQuery();
    }

    // Translate selectedFacets to backend params.
    // Active: single bool — UI lets the user check Active or Inactive (or
    // neither = no filter). Both checked is also "no filter".
    const opts = {};
    const activeSel = selectedFacets.active || [];
    if (activeSel.length === 1) {
      opts.active = activeSel[0] === 'Active' ? 'true' : 'false';
    }
    const chamberSel = selectedFacets.chamber || [];
    if (chamberSel.length === 1) {
      opts.chamber = chamberSel[0];
    }

    setLoading(true);
    try {
      const data = await searchParties(q, 1, 100, opts);
      setResults(data);
      const newParams = { q, mode: searchMode };
      if (perPage !== DEFAULT_PAGE_SIZE) newParams.pageSize = perPage;
      setSearchParams(newParams);
      // Active + Chamber facets come from the backend (corpus-wide counts).
      // First-Letter is client-side from the returned hits.
      setFacets({
        active: data.facets?.active || [],
        chambers: data.facets?.chambers || [],
        firstLetter: data.hits ? computeFirstLetter(data.hits) : {}
      });
      setPage(1);
    } catch (error) {
      console.error('Search error:', error);
    }
    setLoading(false);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    setPage(1);
    const newParams = { q: query || '*', mode: searchMode };
    if (newSize !== DEFAULT_PAGE_SIZE) newParams.pageSize = newSize;
    setSearchParams(newParams);
  };

  const handleReset = () => {
    setQuery('');
    setForm({
      keywords: '',
      name: '',
      abbreviation: '',
      active: 'all'
    });
    setResults(null);
    setSearchParams({});
    // Reset to the page's default state: Active selected, nothing else.
    setSelectedFacets({ active: ['Active'] });
    setFacets({ active: [], chambers: [], firstLetter: {} });
    setPage(1);
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Political Parties</Typography>
        <ToggleButtonGroup
          value={searchMode}
          exclusive
          onChange={handleModeChange}
          size="small"
        >
          <ToggleButton value="simple">
            <SearchIcon sx={{ mr: 1 }} /> Simple
          </ToggleButton>
          <ToggleButton value="advanced">
            <TuneIcon sx={{ mr: 1 }} /> Advanced
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Simple Search */}
      <Collapse in={searchMode === 'simple'}>
        <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Search parties..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ borderRadius: 3, px: 4 }}
            >
              Search
            </Button>
            <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 3 }}>
              Reset
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Advanced Search */}
      <Collapse in={searchMode === 'advanced'}>
        <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords"
                value={form.keywords}
                onChange={handleChange('keywords')}
                placeholder="Search across all fields"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.names}
                value={form.name}
                onChange={handleAutocompleteChange('name')}
                renderInput={(params) => <TextField {...params} label="Party Name" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.abbreviations}
                value={form.abbreviation}
                onChange={handleAutocompleteChange('abbreviation')}
                renderInput={(params) => <TextField {...params} label="Abbreviation" placeholder="e.g., ALP, LP" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Active Status</InputLabel>
                <Select value={form.active} onChange={handleChange('active')} label="Active Status">
                  <MenuItem value="all">All Parties</MenuItem>
                  <MenuItem value="true">Active Only</MenuItem>
                  <MenuItem value="false">Inactive Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => handleSearch()}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 2 }}>
                  Reset
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  Query: <code>{buildAdvancedQuery() || '(empty)'}</code>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {results && results.hits && (() => {
        const filteredHits = filterResults(results.hits);
        const totalPages = Math.ceil(filteredHits.length / perPage);
        const paginatedHits = filteredHits.slice((page - 1) * perPage, page * perPage);
        const hasActiveFilters = Object.values(selectedFacets).some(arr => arr && arr.length > 0);

        return (
          <Grid container spacing={3}>
            {/* Facets Sidebar */}
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterListIcon color="primary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Filters</Typography>
                  </Box>
                  {hasActiveFilters && (
                    <Button size="small" onClick={clearAllFilters}>Clear All</Button>
                  )}
                </Box>

                {/* Active Facet — PRIMARY discriminator. Backend-driven
                    counts so each bucket always renders even when the other
                    bucket is selected. */}
                {facets.active && facets.active.length > 0 && (
                  <Accordion defaultExpanded sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Active</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      {facets.active.map((bucket) => {
                        // Backend returns {value: 'True'|'False', count}.
                        // Render with friendlier UI labels Active/Inactive.
                        const label = bucket.value === 'True' ? 'Active' : 'Inactive';
                        return (
                          <FormControlLabel
                            key={bucket.value}
                            control={
                              <Checkbox
                                size="small"
                                checked={(selectedFacets.active || []).includes(label)}
                                onChange={() => handleFacetClick('active', label)}
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {label} ({bucket.count})
                              </Typography>
                            }
                            sx={{ display: 'flex', m: 0 }}
                          />
                        );
                      })}
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Chamber Facet — derived from current parliamentarians.
                    Member / Senator. Both empty = no chamber filter. */}
                {facets.chambers && facets.chambers.length > 0 && (
                  <Accordion sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Chamber</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      {facets.chambers.map((bucket) => (
                        <FormControlLabel
                          key={bucket.value}
                          control={
                            <Checkbox
                              size="small"
                              checked={(selectedFacets.chamber || []).includes(bucket.value)}
                              onChange={() => handleFacetClick('chamber', bucket.value)}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {bucket.value} ({bucket.count})
                            </Typography>
                          }
                          sx={{ display: 'flex', m: 0 }}
                        />
                      ))}
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* First Letter Facet */}
                {facets.firstLetter && Object.keys(facets.firstLetter).length > 0 && (
                  <Accordion sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>First Letter</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      {Object.entries(facets.firstLetter)
                        .sort((a, b) => a[0].localeCompare(b[0]))
                        .map(([value, count]) => (
                          <FormControlLabel
                            key={value}
                            control={
                              <Checkbox
                                size="small"
                                checked={(selectedFacets.firstLetter || []).includes(value)}
                                onChange={() => handleFacetClick('firstLetter', value)}
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {value} ({count})
                              </Typography>
                            }
                            sx={{ display: 'flex', m: 0 }}
                          />
                        ))}
                    </AccordionDetails>
                  </Accordion>
                )}
              </Paper>
            </Grid>

            {/* Results */}
            <Grid item xs={12} md={9}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="subtitle1">
                    {filteredHits.length} parties found
                    {hasActiveFilters && ` (filtered from ${results.total})`}
                  </Typography>
                  <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                </Box>
                <List>
                  {paginatedHits.map((hit, index) => (
                      <React.Fragment key={hit.id}>
                        {index > 0 && <Divider />}
                        <ListItem alignItems="flex-start" sx={{ flexDirection: 'column' }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {hit.Colour && (
                                  <Box
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: '50%',
                                      bgcolor: hit.Colour,
                                      border: '1px solid #ccc'
                                    }}
                                  />
                                )}
                                <Typography
                                  variant="resultTitle"
                                  component={RouterLink}
                                  to={`/parties/${hit.pID}`}
                                  sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                  {hit.Name}
                                </Typography>
                                {hit.Abbrev && (
                                  <Chip label={hit.Abbrev.trim()} size="small" variant="outlined" />
                                )}
                                {hit.Active === false && (
                                  <Chip label="Inactive" size="small" variant="outlined" color="default" />
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                  ))}
                </List>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(e, value) => setPage(value)}
                      color="primary"
                    />
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        );
      })()}
    </Box>
  );
}
